import { useTranslation } from "react-i18next";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import { WidgetCard, WidgetCardHeader } from "../ProjectLayout/ui/WidgetCard";
import { Formula } from "./_LedgerCells";

export function ChecksPanel({
  relations,
  byKey,
  narrow,
  onReveal
}: {
  relations: TLedgerFigure[];
  byKey: Map<string, TLedgerFigure>;
  narrow: boolean;
  onReveal: (key: string) => void;
}) {
  const { t } = useTranslation("projectHome");
  const holding = relations.filter((relation) => relation.holds === true).length;
  return (
    <WidgetCard data-testid="model-ledger-checks">
      <WidgetCardHeader
        className="pe-5"
        title={t("modelLedger.checksTitle")}
        actions={
          <span
            className={cn("typo-caption", holding === relations.length ? "text-muted-foreground" : "text-destructive")}
          >
            {t("modelLedger.checksSummary", { holding, total: relations.length })}
          </span>
        }
      />
      <ul>
        {relations.map((relation) => {
          const state = relation.error ? "notEvaluated" : relation.holds ? "holds" : "fails";
          return (
            <li
              key={relation.key}
              data-testid={`ledger-check-${relation.key}`}
              data-state={state}
              className={cn(
                "grid items-center gap-x-4 border-t border-foreground/5 px-5 py-3 first:border-t-0",
                narrow ? "grid-cols-[minmax(0,1fr)_auto] gap-y-2" : "grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_7rem]"
              )}
            >
              <span className="typo-label-md wrap-break-word text-foreground">{relation.name}</span>
              <div className={cn("min-w-0", narrow && "col-span-2 row-start-2")}>
                {relation.formula && <Formula formula={relation.formula} byKey={byKey} onReveal={onReveal} />}
                {relation.error && <span className="typo-caption block text-destructive">{relation.error}</span>}
              </div>
              <span
                className={cn(
                  "typo-label-sm text-end",
                  state === "holds" ? "text-success" : state === "fails" ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {t(
                  state === "holds"
                    ? "modelLedger.checkHolds"
                    : state === "fails"
                    ? "modelLedger.checkFails"
                    : "modelLedger.checkNotEvaluated"
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </WidgetCard>
  );
}
